/*
  You Fly LA — body photos beside the text (theme v45, 2026-09-26). Alex picked option A:
  "no single large image on the pages … make them smaller and interactive, side by side. Don't remove the text,
  don't remove the images."

  WHAT IT DOES. Every photo that stands alone in a page, course, city, aircraft, longform or blog-post body
  (a <figure>, a <p> holding only the image, or a bare <img>, as written by the content editors) is turned into:
    - "side": a compact photo beside the paragraphs that follow it (alternating right / left down the page),
      when enough text follows before the next heading, table or widget to sit beside it;
    - "solo": a compact centered photo otherwise (image right before a heading, at the end of a section, etc.).
  Every photo becomes a button that opens a full-size viewer (dialog) with the caption, "N / total", previous/next
  and Esc / arrow keys. Phones get a small thumbnail inside the paragraph instead of a full-width photo.

  RULES KEPT. Nothing is removed: the image node, its <figure>/<p> and <figcaption> are kept; the script adds
  classes, one <button> around the image and the "Tap To Enlarge" tag. The only move: a photo placed at the end of a
  section with no text after it is moved up within the same section (never past a heading, table or widget) to sit
  beside the paragraphs before it (marked data-yfm-moved="up"). Headings, FAQ markup and structured data are untouched (the change is client-side only). Widgets
  ([data-yfla-mod]), tables and lists are never touched. Idempotent (data-yfm-root); a failure leaves the page
  exactly as it was. Styles: assets/yfla-media.css (also holds a no-JS height cap for the same photos).
*/
(function () {
  'use strict';
  if (window.YFLAMedia) return;

  var ROOTS = '.page-content, article.prose, article.prose-wide, .blog-post-content';
  var SIDE_MIN = 240;   /* characters of following text needed to sit a photo beside it */
  var SMALL_BELOW = 700; /* fewer characters than this: the smaller side size */
  var PULL_TARGET = 1100; /* when pulling a photo up, gather about this much text to sit beside */
  var TALL_MIN = 1800;   /* a portrait keeps its tall 4:5 frame only beside this much text (else 4:3) */
  var ICON = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>';

  function isWidget(el) {
    return el.hasAttribute('data-yfla-mod') || !!el.querySelector('[data-yfla-mod]');
  }

  /* The one <img> of a standalone photo block, or null. */
  function imgOf(c) {
    if (!c || c.nodeType !== 1 || c.classList.contains('yfm')) return null;
    var tag = c.tagName;
    if (tag === 'IMG') return c;
    if (tag !== 'FIGURE' && tag !== 'P') return null;
    if (isWidget(c)) return null;
    var imgs = c.getElementsByTagName('img');
    if (imgs.length !== 1) return null;
    var img = imgs[0];
    if (img.closest('a, table, ul, ol')) return null;
    var t = c.textContent || '';
    var cap = c.querySelector('figcaption');
    if (cap) t = t.replace(cap.textContent || '', '');
    if (t.replace(/\s+/g, '').length) return null;
    return img;
  }

  /* How much running text follows the photo before something that must not sit beside it. */
  function textAfter(box) {
    var n = 0, el = box.nextElementSibling;
    while (el) {
      if (imgOf(el)) break;
      var tag = el.tagName;
      if (/^(H1|H2|H3|H4|H5|H6|TABLE|HR|FIGURE|SECTION|DETAILS|FORM)$/.test(tag)) break;
      if (isWidget(el) || el.classList.contains('yfla-table-wrap')) break;
      if (isText(el)) {
        n += (el.textContent || '').replace(/\s+/g, ' ').trim().length;
      } else break;
      el = el.nextElementSibling;
    }
    return n;
  }

  function isText(el) {
    var tag = el.tagName;
    return tag === 'P' || tag === 'UL' || tag === 'OL' || tag === 'BLOCKQUOTE' || (tag === 'DIV' && el.classList.contains('yfla-note'));
  }

  /* Editors usually put the photo at the END of a section, right before the next heading. When too little text
     follows it, the photo is moved up (same section, never across a heading, table or widget) to sit beside the
     paragraphs that come before it. Returns the element to insert before, or null. */
  function pullUpTarget(box) {
    var n = 0, first = null, el = box.previousElementSibling;
    while (el) {
      if (imgOf(el) || el.classList.contains('yfm') || isWidget(el) || !isText(el)) break;
      n += (el.textContent || '').replace(/\s+/g, ' ').trim().length;
      first = el;
      if (n >= PULL_TARGET) break;
      el = el.previousElementSibling;
    }
    return n >= SIDE_MIN ? first : null;
  }

  function bestSrc(img) {
    var ss = img.getAttribute('srcset');
    if (ss) {
      var best = null, bw = 0;
      ss.split(',').forEach(function (part) {
        var bits = part.trim().split(/\s+/), w = parseInt(bits[1], 10) || 0;
        if (bits[0] && w >= bw) { bw = w; best = bits[0]; }
      });
      if (best) return best;
    }
    return img.currentSrc || img.getAttribute('src') || '';
  }

  /* ---------- viewer ---------- */
  var LB, LBimg, LBcap, LBn, LBprev, LBnext, LBlist = [], LBi = 0, LBfrom = null;

  function ensureViewer() {
    if (LB) return;
    LB = document.createElement('dialog');
    LB.className = 'yfm-lb';
    LB.setAttribute('aria-label', 'Photo viewer');
    LB.innerHTML =
      '<div class="yfm-lb-in">' +
        '<img class="yfm-lb-img" alt="">' +
        '<div class="yfm-lb-bar">' +
          '<p class="yfm-lb-cap"></p>' +
          '<div class="yfm-lb-ctl">' +
            '<span class="yfm-lb-n" aria-live="polite"></span>' +
            '<button type="button" class="yfm-lb-b" data-d="-1">Previous</button>' +
            '<button type="button" class="yfm-lb-b" data-d="1">Next</button>' +
            '<button type="button" class="yfm-lb-b yfm-lb-x">Close</button>' +
          '</div>' +
        '</div>' +
      '</div>';
    document.body.appendChild(LB);
    LBimg = LB.querySelector('.yfm-lb-img');
    LBcap = LB.querySelector('.yfm-lb-cap');
    LBn = LB.querySelector('.yfm-lb-n');
    LBprev = LB.querySelector('[data-d="-1"]');
    LBnext = LB.querySelector('[data-d="1"]');
    LB.addEventListener('click', function (e) {
      if (e.target === LB || e.target.classList.contains('yfm-lb-in')) { closeViewer(); return; }
      var b = e.target.closest ? e.target.closest('button') : null;
      if (!b) return;
      if (b.classList.contains('yfm-lb-x')) { closeViewer(); return; }
      if (b.hasAttribute('data-d')) show(LBi + parseInt(b.getAttribute('data-d'), 10));
    });
    LB.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); show(LBi + 1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); show(LBi - 1); }
      else if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); closeViewer(); }
    });
    /* The theme swallows the dialog's own close/cancel events on this site, so cleanup never waits for them;
       the listener stays only as a backstop for any other close path. */
    LB.addEventListener('close', cleanup);
  }

  function cleanup() {
    document.documentElement.classList.remove('yfm-lock');
    var from = LBfrom; LBfrom = null;
    if (from && from.focus) { try { from.focus({ preventScroll: true }); } catch (err) { from.focus(); } }
  }

  function show(i) {
    var n = LBlist.length;
    if (!n) return;
    LBi = (i % n + n) % n;
    var it = LBlist[LBi];
    LBimg.src = it.src;
    LBimg.alt = it.alt || '';
    LBcap.textContent = it.cap || it.alt || '';
    LBn.textContent = n > 1 ? (LBi + 1) + ' / ' + n : '';
    LBprev.hidden = LBnext.hidden = n < 2;
  }

  function openViewer(list, i, from) {
    ensureViewer();
    LBlist = list; LBfrom = from || null;
    show(i);
    if (typeof LB.showModal === 'function') { if (!LB.open) LB.showModal(); }
    else LB.setAttribute('open', '');
    document.documentElement.classList.add('yfm-lock');
    var x = LB.querySelector('.yfm-lb-x');
    if (x) x.focus();
  }

  function closeViewer() {
    if (!LB) return;
    if (typeof LB.close === 'function' && LB.open) LB.close();
    else LB.removeAttribute('open');
    cleanup();
  }

  /* ---------- body photos ---------- */
  function build(root, list) {
    var kids = Array.prototype.slice.call(root.children);
    var side = 0;
    kids.forEach(function (c) {
      var img = imgOf(c);
      if (!img) return;
      var box = c;
      if (c.tagName === 'IMG') {
        box = document.createElement('figure');
        c.parentNode.insertBefore(box, c);
        box.appendChild(c);
      }
      var w = parseInt(img.getAttribute('width'), 10) || img.naturalWidth || 4;
      var h = parseInt(img.getAttribute('height'), 10) || img.naturalHeight || 3;
      var r = h / w;
      var shape = r > 1.1 ? 'port' : (r < 0.9 ? 'land' : 'sq');
      var L = textAfter(box);
      if (L < SIDE_MIN) {
        var up = pullUpTarget(box);
        if (up) {
          up.parentNode.insertBefore(box, up);
          box.setAttribute('data-yfm-moved', 'up');
          L = textAfter(box);
        }
      }
      var mode = L >= SIDE_MIN ? 'side' : 'solo';
      box.classList.add('yfm', 'yfm--' + mode, 'yfm--' + shape);
      if (mode === 'side') {
        box.classList.add(side % 2 === 0 ? 'yfm--r' : 'yfm--l');
        if (L < SMALL_BELOW) box.classList.add('yfm--sm');
        else if (shape === 'port' && L >= TALL_MIN) box.classList.add('yfm--tall');
        side++;
      }
      var alt = (img.getAttribute('alt') || '').trim();
      var capEl = box.querySelector('figcaption');
      var cap = capEl ? (capEl.textContent || '').trim() : '';
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'yfm-btn';
      btn.setAttribute('aria-label', 'Enlarge photo' + (alt ? ': ' + alt : ''));
      img.parentNode.insertBefore(btn, img);
      btn.appendChild(img);
      var tagEl = document.createElement('span');
      tagEl.className = 'yfm-tag';
      tagEl.setAttribute('aria-hidden', 'true');
      tagEl.innerHTML = ICON + '<span>Tap To Enlarge</span>';
      btn.appendChild(tagEl);
      var idx = list.length;
      list.push({ src: bestSrc(img), alt: alt, cap: cap });
      btn.addEventListener('click', function () { openViewer(list, idx, btn); });
    });
  }

  /* Blog-post featured image (theme block above the body): capped in CSS, opens in the viewer. */
  function featured(list) {
    var img = document.querySelector('main[data-template="article"] .image-block__image');
    if (!img || img.closest('.yfm-btn') || img.hasAttribute('data-yfm')) return;
    img.setAttribute('data-yfm', '1');
    img.setAttribute('role', 'button');
    img.setAttribute('tabindex', '0');
    var alt = (img.getAttribute('alt') || '').trim();
    img.setAttribute('aria-label', 'Enlarge photo' + (alt ? ': ' + alt : ''));
    var item = { src: bestSrc(img), alt: alt, cap: alt };
    var go = function () { openViewer([item].concat(list), 0, img); };
    img.addEventListener('click', go);
    img.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(); } });
  }

  function init() {
    var list = [];
    Array.prototype.forEach.call(document.querySelectorAll(ROOTS), function (el) {
      if (el.closest('[data-yfm-root]')) return;
      var root = el.querySelector(':scope > rte-formatter') || el;
      if (root.hasAttribute('data-yfm-root')) return;
      root.setAttribute('data-yfm-root', '1');
      root.classList.add('yfm-root');
      try { build(root, list); } catch (err) { if (window.console) console.warn('[yfla-media]', err); }
    });
    try { featured(list); } catch (err) { if (window.console) console.warn('[yfla-media]', err); }
  }

  window.YFLAMedia = { init: init, open: openViewer };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
